/* eslint-disable no-console */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Loader, Button, Message, Icon } from "semantic-ui-react";
import moment from "moment";
import { useAuth } from "../../../../hooks";
import { useAttendanceData } from "./hooks/useAttendance";
import {
  calculateAttendanceStats,
  getCycleRange,
  createMonthlyAttendanceMap,
  STATUS_CONFIG,
  DEFAULT_STATUS,
} from "./utils/attendanceUtils";
import "./AttendanceHistory.scss";

// Configurar moment en español globalmente
moment.locale("es");

// Fecha de hoy según el reloj local del navegador, normalizada a UTC sin hora
// (evita que moment.utc() sin argumentos devuelva el día siguiente cuando el
// offset local ya cruzó la medianoche UTC, ej. después de las 19:00 en Perú)
const getTodayUtc = () => moment.utc(moment().format("YYYY-MM-DD"));

const getInitialMonthWithinCycle = (academicCycle) => {
  const { startDate, endDate } = getCycleRange(academicCycle);
  const today = getTodayUtc();

  if (today.isBefore(startDate, "month")) {
    return startDate.format("YYYY-MM");
  }

  if (today.isAfter(endDate, "month")) {
    return endDate.format("YYYY-MM");
  }

  return today.format("YYYY-MM");
};

// Componentes auxiliares
const UnifiedHeader = ({
  stats,
  selectedMonth,
  canNavigatePrevious,
  canNavigateNext,
  onNavigatePrevious,
  onNavigateNext,
  cycleRange,
}) => (
  <div className="attendance-history__unified-header">
    {/* Botón izquierda */}
    <Button
      icon="chevron left"
      onClick={onNavigatePrevious}
      disabled={!canNavigatePrevious}
      title="Mes anterior"
      className="attendance-history__nav-btn attendance-history__nav-btn--prev"
      primary
    />

    {/* Título y rango de fecha */}
    <div className="attendance-history__header-info">
      <h3 className="attendance-history__month-title">
        {moment.utc(selectedMonth).format("MMMM YYYY").charAt(0).toUpperCase() +
          moment.utc(selectedMonth).format("MMMM YYYY").slice(1)}
      </h3>
      {cycleRange && (
        <span className="attendance-history__date-range">
          <Icon name="calendar alternate outline" />
          {`${cycleRange.startDate.format(
            "DD [de] MMMM"
          )} - ${cycleRange.endDate.format("DD [de] MMMM")}`}
        </span>
      )}
    </div>

    {/* Resumen de estadísticas */}
    <div className="attendance-history__stats-container">
      <div className="attendance-history__stat-item attendance-history__stat-item--percentage">
        <span className="attendance-history__stat-value">{stats.percentage}</span>
        <span className="attendance-history__stat-label">Asistencia</span>
      </div>
      <div className="attendance-history__stat-item">
        <span className="attendance-history__stat-value">{stats.total}</span>
        <span className="attendance-history__stat-label">Total</span>
      </div>
      <div className="attendance-history__stat-item attendance-history__stat-item--present">
        <span className="attendance-history__stat-value">{stats.present}</span>
        <span className="attendance-history__stat-label">Presentes</span>
      </div>
      <div className="attendance-history__stat-item attendance-history__stat-item--late">
        <span className="attendance-history__stat-value">{stats.late}</span>
        <span className="attendance-history__stat-label">Tardanzas</span>
      </div>
      <div className="attendance-history__stat-item attendance-history__stat-item--absent">
        <span className="attendance-history__stat-value">{stats.absent}</span>
        <span className="attendance-history__stat-label">Faltas</span>
      </div>
      <div className="attendance-history__stat-item attendance-history__stat-item--justified">
        <span className="attendance-history__stat-value">{stats.justified}</span>
        <span className="attendance-history__stat-label">Justificadas</span>
      </div>
    </div>

    {/* Botón derecha */}
    <Button
      icon="chevron right"
      onClick={onNavigateNext}
      disabled={!canNavigateNext}
      title="Mes siguiente"
      className="attendance-history__nav-btn attendance-history__nav-btn--next"
      primary
    />
  </div>
);

const CalendarDay = ({
  date,
  attendance,
  isCurrentMonth,
  isToday,
  isWithinCycle,
}) => {
  const statusInfo = attendance
    ? STATUS_CONFIG[attendance.status] || DEFAULT_STATUS
    : null;

  let dayClass = "attendance-calendar__day";
  if (!isCurrentMonth) dayClass += " attendance-calendar__day--other-month";
  if (isToday) dayClass += " attendance-calendar__day--today";
  if (!isWithinCycle) dayClass += " attendance-calendar__day--out-of-range";
  if (statusInfo) dayClass += ` attendance-calendar__day--${statusInfo.color}`;
  else if (isCurrentMonth) dayClass += " attendance-calendar__day--no-record";

  return (
    <div className={dayClass}>
      <div className="attendance-calendar__day-header">
        <div className="attendance-calendar__day-number">
          {date.format("D")}
        </div>
        <span className="attendance-calendar__day-name">
          {date.format("ddd")}
        </span>
      </div>

      {attendance && isCurrentMonth && isWithinCycle && statusInfo && (
        <div className="attendance-calendar__day-content">
          {attendance.arrivalTime && (
            <div className="attendance-calendar__time">
              <Icon name="clock outline" />
              <span>{attendance.arrivalTime}</span>
            </div>
          )}
          {attendance.notes && (
            <div className="attendance-calendar__notes">
              <Icon name="sticky note outline" />
              <span>{attendance.notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Calendar = ({ monthlyAttendance, selectedMonth, cycleRange }) => {
  const headerDays = useMemo(() => {
    const days = [];
    for (let i = 1; i <= 5; i++) {
      days.push(
        <div key={i} className="attendance-calendar__day-header-table">
          {moment.utc().day(i).format("dddd").charAt(0).toUpperCase() +
            moment.utc().day(i).format("dddd").slice(1)}
        </div>
      );
    }
    return days;
  }, []);

  const calendarWeeks = useMemo(() => {
    const monthMoment = moment.utc(selectedMonth, "YYYY-MM", true);
    if (!monthMoment.isValid()) return [];

    const startOfMonth = monthMoment.clone().startOf("month");
    const endOfMonth = monthMoment.clone().endOf("month");
    const cycleStart = cycleRange?.startDate?.clone();
    const cycleEnd = cycleRange?.endDate?.clone();
    const weeks = [];

    let weekStart = startOfMonth.clone();

    while (weekStart.isoWeekday() !== 1) {
      weekStart.subtract(1, "day");
    }

    while (weekStart.isSameOrBefore(endOfMonth, "day")) {
      const week = [];

      for (let dayIndex = 0; dayIndex < 5; dayIndex++) {
        const currentDate = weekStart.clone().add(dayIndex, "day");
        const dateKey = currentDate.format("YYYY-MM-DD");
        const isCurrentMonth = currentDate.isSame(monthMoment, "month");
        const isToday = currentDate.isSame(getTodayUtc(), "day");
        const isWithinCycle =
          !cycleStart || !cycleEnd
            ? true
            : currentDate.isSameOrAfter(cycleStart, "day") &&
              currentDate.isSameOrBefore(cycleEnd, "day");
        const attendance =
          isWithinCycle && isCurrentMonth ? monthlyAttendance[dateKey] : null;

        week.push(
          <CalendarDay
            key={dateKey}
            date={currentDate}
            attendance={attendance}
            isCurrentMonth={isCurrentMonth}
            isToday={isToday}
            isWithinCycle={isWithinCycle}
          />
        );
      }

      weeks.push(
        <div
          key={weekStart.format("GGGG-[W]WW")}
          className="attendance-calendar__week"
        >
          {week}
        </div>
      );

      weekStart.add(7, "days");
    }

    return weeks;
  }, [monthlyAttendance, selectedMonth, cycleRange]);

  return (
    <div className="attendance-calendar">
      <div className="attendance-calendar__header">{headerDays}</div>
      <div className="attendance-calendar__body">{calendarWeeks}</div>
    </div>
  );
};

const AttendanceLegend = () => (
  <div className="attendance-history__legend">
    <div className="attendance-legend">
      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
        <div key={key} className="attendance-legend__item">
          <div
            className={`attendance-legend__icon attendance-legend__icon--${config.color}`}
          >
            <i className={`fas fa-${config.icon}`}></i>
          </div>
          <span>{config.text}</span>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState = () => (
  <div className="attendance-history__empty">
    <div className="attendance-history__empty-content">
      <i className="fas fa-calendar-times"></i>
      <h3>No hay registros de asistencia</h3>
      <p>
        No se encontraron registros de asistencia para este estudiante en el
        periodo seleccionado.
      </p>
    </div>
  </div>
);

export function AttendanceHistory(props) {
  const { student, gradeId, academicCycleId, academicCycle, onClose } = props;
  const { accessToken } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(() =>
    getInitialMonthWithinCycle(academicCycle)
  );

  // Custom hook para manejar los datos de asistencia
  const { attendanceData, loading, error } = useAttendanceData(
    accessToken,
    student.id,
    academicCycleId
  );

  // Funciones utilitarias memoizadas
  const cycleRange = useMemo(
    () => getCycleRange(academicCycle),
    [academicCycle]
  );

  useEffect(() => {
    const ensureMonthWithinCycle = () => {
      const { startDate, endDate } = cycleRange;
      return setSelectedMonth((prev) => {
        const parsedPrev = moment.utc(prev, "YYYY-MM", true);

        if (!parsedPrev.isValid()) {
          return getInitialMonthWithinCycle(academicCycle);
        }

        if (parsedPrev.isBefore(startDate, "month")) {
          return startDate.format("YYYY-MM");
        }

        if (parsedPrev.isAfter(endDate, "month")) {
          return endDate.format("YYYY-MM");
        }

        return prev;
      });
    };

    ensureMonthWithinCycle();
  }, [cycleRange, academicCycle]);

  const attendanceStats = useMemo(
    () => calculateAttendanceStats(attendanceData),
    [attendanceData]
  );

  const monthlyAttendance = useMemo(
    () =>
      createMonthlyAttendanceMap(
        attendanceData?.attendanceRecords,
        selectedMonth
      ),
    [attendanceData, selectedMonth]
  );

  // Funciones de navegación
  const canNavigatePrevious = useCallback(() => {
    const prevMonth = moment.utc(selectedMonth, "YYYY-MM", true).subtract(
      1,
      "month"
    );
    return prevMonth.isSameOrAfter(cycleRange.startDate, "month");
  }, [selectedMonth, cycleRange.startDate]);

  const canNavigateNext = useCallback(() => {
    const nextMonth = moment.utc(selectedMonth, "YYYY-MM", true).add(1, "month");
    return nextMonth.isSameOrBefore(cycleRange.endDate, "month");
  }, [selectedMonth, cycleRange.endDate]);

  const navigatePrevious = useCallback(() => {
    if (canNavigatePrevious()) {
      const prevMonth = moment.utc(selectedMonth, "YYYY-MM", true)
        .subtract(1, "month")
        .format("YYYY-MM");
      setSelectedMonth(prevMonth);
    }
  }, [selectedMonth, canNavigatePrevious]);

  const navigateNext = useCallback(() => {
    if (canNavigateNext()) {
      const nextMonth = moment.utc(selectedMonth, "YYYY-MM", true)
        .add(1, "month")
        .format("YYYY-MM");
      setSelectedMonth(nextMonth);
    }
  }, [selectedMonth, canNavigateNext]);

  // Verificar si hay datos válidos
  const hasAttendanceData = attendanceData?.attendanceRecords?.length > 0;

  // Render condicional para estado de carga
  if (loading) return <Loader active inline="centered" />;

  return (
    <div className="attendance-history">
      {error && <Message negative content={error} />}

      {/* Header unificado: navegación + título + estadísticas */}
      <UnifiedHeader
        stats={attendanceStats}
        selectedMonth={selectedMonth}
        canNavigatePrevious={canNavigatePrevious()}
        canNavigateNext={canNavigateNext()}
        onNavigatePrevious={navigatePrevious}
        onNavigateNext={navigateNext}
        cycleRange={cycleRange}
      />

      {/* Calendario */}
      <div className="attendance-history__calendar-section">
        {!hasAttendanceData ? (
          <EmptyState />
        ) : (
          <>
            <Calendar
              monthlyAttendance={monthlyAttendance}
              selectedMonth={selectedMonth}
              cycleRange={cycleRange}
            />

            {/* Leyenda como footer */}
            <AttendanceLegend />
          </>
        )}
      </div>
    </div>
  );
}
